// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/ReconciliationPage.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the page
// identity moved from the predecessor's HeaderTitle slot into the workspace frame's tab bar,
// so the content opens on the status-and-actions row. The `?tab=` deep link, no-fallback run
// selection, withheld/spillover one-liners and the two-tab body carry unchanged.
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeftRight, Info, ListChecks } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { api } from '../../api/client'
import {
  useDifferenceSummary,
  usePeriodSummary,
  useReconciliation,
  useRun,
  useRuns,
} from '../../api/hooks'
import { pendingLine, staleAliasLine, withheldLine } from '../../api/reconChips'
import type { DiffStatus } from '../../api/types'
import { AutoMatchPanel } from '../../components/AutoMatchPanel'
import { Chip, type Tone } from '../../components/badges'
import { CompanyWorkbench } from '../../components/CompanyWorkbench'
import { DifferenceView } from '../../components/DifferenceView'
import { Drawer } from '../../components/Drawer'
import { InfoTip } from '../../components/InfoTip'
import { Money } from '../../components/Money'
import { NoRunLoaded } from '../../components/NoRunLoaded'
import { ProcessingPanel } from '../../components/ProcessingPanel'
import { ReconcileButton } from '../../components/ReconcileButton'
import { ReconProgressMeter } from '../../components/ReconProgressMeter'
import { RunInsights } from '../../components/RunInsights'
import { RunSummary } from '../../components/RunSummary'
import { runTag } from '../../format'
import { useRunSelection } from '../../workspace/runContext'

const fmt = new Intl.NumberFormat('en-IN')

const BUCKET_META: Record<DiffStatus, { label: string; tone: Tone }> = {
  ALL_MATCH: { label: 'All match', tone: 'green' },
  USER_MATCH: { label: 'User matched', tone: 'violet' },
  DIFF: { label: 'Difference', tone: 'red' },
  ONLY_26AS: { label: 'In 26AS, not books', tone: 'orange' },
  ONLY_BOOKS: { label: 'In books, not 26AS', tone: 'yellow' },
}

export function ReconciliationPage() {
  const { clientId, periodId } = useParams()
  const pid = periodId ?? ''
  const base = `/w/${clientId}/${pid}`
  const [params, setParams] = useSearchParams()
  // which panel is open; RunInsights needs to know, because a hidden panel
  // must not keep two full-scan endpoints warm
  const [panel, setPanel] = useState<string | null>(null)
  // the open tab lives in the URL, so a link can name a surface and the back
  // button moves between them
  const raw = params.get('tab') ?? 'worklist'
  // cleared/followup tabs were retired — old links land on the worklist
  const tab = raw === 'cleared' || raw === 'followup' ? 'worklist' : raw
  const setTab = (v: string) => {
    const next = new URLSearchParams(params)
    next.set('tab', v)
    setParams(next, { replace: true })
  }

  const summaryQ = usePeriodSummary(pid)
  const runsQ = useRuns(pid)
  const completeRuns = (runsQ.data ?? []).filter((r) => r.status === 'complete')
  // no fallback to completeRuns[0] — see NoRunLoaded. `useRunSelection` still
  // restores an explicit choice made earlier in THIS session.
  const { runId, setRun } = useRunSelection(pid)
  const runQ = useRun(runId ?? '')
  const reconQ = useReconciliation(runId)
  const run = runQ.data ?? completeRuns.find((r) => r.id === runId)
  const activeRuns = (runsQ.data ?? []).filter(
    (r) => r.status === 'queued' || r.status === 'running',
  )
  const activeQ = useRun(activeRuns[0]?.id ?? '')
  const sumQ = useDifferenceSummary(run?.status === 'complete' ? runId : null)
  const s = sumQ.data
  // what OTHER years' reconciliations said about this one: timing explains
  // elsewhere whose target_period names this FY
  const spillQ = useQuery({
    queryKey: ['spillover', pid],
    queryFn: () => api<{
      items: { resolution_id: string; item: string | null;
        amount: string | null; origin_period_label: string | null;
        target_period: string | null }[]
      total: string
    }>(`/periods/${pid}/spillover`),
    enabled: Boolean(pid),
  })
  const spill = spillQ.data

  const stale = staleAliasLine(reconQ.data?.aliases_since_run ?? [])

  return (
    <div className="space-y-4">
      {(completeRuns.length > 0 || (run && run.status === 'complete')) && (
        <div className="flex flex-nowrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-4">
            {/* progress is status, and status earns a line, not a banner */}
            {run && run.status === 'complete' && (
              <ReconProgressMeter runId={runId} compact />
            )}
            {/* pending approval is a check-occasionally figure, not an
                act-on band */}
            {s && pendingLine(s.pending_matches) && (
              <p className="whitespace-nowrap text-xs font-semibold text-violet-700">
                {pendingLine(s.pending_matches)}
              </p>
            )}
          </div>
          <div className="flex flex-nowrap items-center gap-2">
            {runId != null && run?.status === 'complete' && (
              <ReconcileButton
                runId={runId}
                aliasesSinceRun={reconQ.data?.aliases_since_run ?? []}
                onStarted={(fresh) => setRun(fresh.id)}
              />
            )}
            {/* After name mapping, not before: a company with entries on only
                one side has no counterpart to match against, so auto-match can
                do nothing for it until an alias joins the two names. */}
            {runId != null && run?.status === 'complete'
              && runQ.data?.book_grain !== 'party' && (
              <AutoMatchPanel runId={runId} />
            )}
            {run && run.status === 'complete' && (
              <button
                type="button"
                className="rounded p-1.5 text-muted-foreground hover:bg-muted"
                title="Run details, tie-out & timing"
                aria-label="Run details"
                onClick={() => setPanel(panel === 'details' ? null : 'details')}
              >
                <Info size={18} />
              </button>
            )}
            {completeRuns.length > 0 && (
              <select
                className="rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
                value={runId ?? ''}
                onChange={(e) => { if (e.target.value) setRun(e.target.value) }}
                aria-label="Run"
              >
                {runId == null && <option value="">Run…</option>}
                {completeRuns.map((r) => (
                  <option key={r.id} value={r.id}>Run {runTag(r.id)}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {activeQ.data && (activeQ.data.status === 'queued' || activeQ.data.status === 'running') && (
        <ProcessingPanel run={activeQ.data} lastDurations={summaryQ.data?.last_step_durations} />
      )}

      {runId == null && !activeQ.data && <NoRunLoaded surface="the reconciliation" />}

      {/* An approved mapping that did not apply is a fact about these figures.
          Leaving it to the alias screen is how someone re-runs twice looking
          for a group that was never in the snapshot. One line, not a band —
          the group list and the why live behind the ⓘ; the ACTION (review)
          keeps its named link. */}
      {(run?.aliases_withheld?.length ?? 0) > 0 && (
        <div className="flex flex-nowrap items-center gap-1.5">
          <AlertTriangle size={16} className="shrink-0 text-orange-600" />
          <p className="text-sm font-semibold text-orange-800">
            {withheldLine(run!.aliases_withheld!.length)}
          </p>
          <InfoTip w={420}>
            Each joins more than one name on a side and nobody has approved
            what it reaches, so those parties are reconciling separately on
            this run:{' '}
            {run!.aliases_withheld!
              .map((w) => `${w.label} (${w.names_26as.length}×${w.names_books.length})`)
              .join(' · ')}
          </InfoTip>
          <Link to={`${base}/aliases`} className="text-sm font-semibold text-primary hover:underline">
            Review on Alias &amp; Rules
          </Link>
        </div>
      )}

      {/* incoming spillover: one line, not a band — the list and the
          instruction live behind the ⓘ */}
      {(spill?.items.length ?? 0) > 0 && (
        <div className="flex flex-nowrap items-center gap-1.5">
          <Info size={16} className="shrink-0 text-blue-600" />
          <p className="text-sm font-semibold text-blue-800">
            {spill!.items.length} entr{spill!.items.length === 1 ? 'y' : 'ies'}{' '}
            explained in other years as arriving in this FY —{' '}
            <Money v={spill!.total} bold />
          </p>
          <InfoTip w={440}>
            Timing explanations from other periods point at this financial
            year — check the amounts are in THIS year&apos;s 26AS:{' '}
            {spill!.items.slice(0, 6)
              .map((x) => `${x.item ?? '?'} — ₹${x.amount ?? '?'} (from ${x.origin_period_label ?? '?'})`)
              .join(' · ')}
            {spill!.items.length > 6 ? ' …' : ''}
          </InfoTip>
        </div>
      )}

      {run && run.status === 'complete' && (
        <>
          {/* run details, tie-out and insights live in a drawer behind the ⓘ
              beside the run picker. Mounted only while open, so RunInsights'
              two full scans still run only when read. */}
          <Drawer
            opened={panel === 'details'}
            onClose={() => setPanel(null)}
            title="Run details, tie-out & timing"
          >
            {panel === 'details' && (
              <div className="space-y-4">
                <RunInsights runId={runId} active />
                {s && (
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    {s.buckets.map((b) => {
                      const m = BUCKET_META[b.status]
                      return (
                        <div key={b.status} className="rounded-lg border border-border p-2.5">
                          <div className="flex items-center justify-between gap-1.5">
                            <Chip tone={m.tone}>{m.label}</Chip>
                            <span className="font-bold text-foreground">{fmt.format(b.n)}</span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">TDS 26AS</p>
                          <Money v={b.tds_26as} bold className="text-xs" />
                        </div>
                      )
                    })}
                  </div>
                )}
                <RunSummary run={run} />
              </div>
            )}
          </Drawer>

          {/* `?tab=` so a link can name a surface. Two tabs, not four: the
              Matched and Unmatched tabs were the same grid with a different
              filter, and so was the whole Comparison page. */}
          <div className="flex border-b border-border">
            {[
              { value: 'worklist', label: 'Worklist — clear exceptions', icon: <ListChecks size={16} /> },
              { value: 'sheet', label: '26AS vs Books', icon: <ArrowLeftRight size={16} /> },
            ].map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={`flex flex-1 items-center justify-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
                  tab === t.value
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {tab === 'worklist' && (
            <div className="space-y-2">
              {stale && (
                <div className="flex flex-nowrap items-center gap-1.5">
                  <AlertTriangle size={16} className="shrink-0 text-amber-600" />
                  <p className="text-sm font-semibold text-amber-800">
                    {stale.label} — {stale.preview}
                  </p>
                  <InfoTip w={380}>
                    An alias joins two names for the <b>next</b> run; it
                    cannot change rows the engine has already written, so the
                    screen will keep showing both sides open. Re-run the
                    reconciliation to apply them.
                  </InfoTip>
                </div>
              )}
              {/* the how-to reads once, then costs a band — folded behind ⓘ;
                  the data starts here instead */}
              <div className="flex items-center gap-1">
                <p className="text-xs font-semibold text-muted-foreground">
                  Companies with work left, biggest difference first
                </p>
                <InfoTip>
                  Open a company to see what the engine can line up and on what
                  grounds — accept a whole band, or one at a time. Nothing is
                  matched until you say so, and anything you leave stays open.
                </InfoTip>
              </div>
              {/* party-grain books — every transaction surface says the one
                  honest sentence instead of pretending */}
              {runQ.data?.book_grain === 'party' && (
                <p className="text-sm font-semibold text-orange-800">
                  Books carry party-level sums only — this reconciliation is
                  at party level; transaction matching is unavailable for
                  this run.
                </p>
              )}
              <CompanyWorkbench runId={runId} grain={runQ.data?.book_grain} />
            </div>
          )}
          {tab === 'sheet' && (
            /* no caption line — the sheet's own controls say what it is; the
               data starts immediately */
            <DifferenceView runId={runId} initialView="unmatched" />
          )}
        </>
      )}
    </div>
  )
}
